/**
 * Theme color definitions for accent/primary colors.
 * Each color has light and dark mode HSL values.
 */

export type ThemeColor =
  | 'neutral'
  | 'amber'
  | 'blue'
  | 'cyan'
  | 'emerald'
  | 'fuchsia'
  | 'green'
  | 'indigo'
  | 'lime'
  | 'orange'
  | 'pink'
  | 'red'
  | 'rose'
  | 'violet'
  | 'yellow'

export interface ThemeColorDefinition {
  id: ThemeColor
  name: string
  description?: string
  /** HSL values: [hue, saturation%, lightness%] */
  light: {
    primary: [number, number, number]
    primaryForeground: [number, number, number]
  }
  dark: {
    primary: [number, number, number]
    primaryForeground: [number, number, number]
  }
  /** Preview color for the selector (CSS color string) */
  preview: string
}

export const THEME_COLORS: ThemeColorDefinition[] = [
  {
    id: 'neutral',
    name: 'Neutral',
    description: 'Match base color',
    light: {
      primary: [0, 0, 9],
      primaryForeground: [0, 0, 98],
    },
    dark: {
      primary: [0, 0, 98],
      primaryForeground: [0, 0, 9],
    },
    preview: 'hsl(0, 0%, 20%)',
  },
  {
    id: 'amber',
    name: 'Amber',
    light: {
      primary: [38, 92, 50],
      primaryForeground: [48, 96, 5],
    },
    dark: {
      primary: [38, 92, 50],
      primaryForeground: [48, 96, 5],
    },
    preview: 'hsl(38, 92%, 50%)',
  },
  {
    id: 'blue',
    name: 'Blue',
    light: {
      primary: [221, 83, 53],
      primaryForeground: [210, 40, 98],
    },
    dark: {
      primary: [217, 91, 60],
      primaryForeground: [222, 47, 11],
    },
    preview: 'hsl(221, 83%, 53%)',
  },
  {
    id: 'cyan',
    name: 'Cyan',
    light: {
      primary: [189, 94, 43],
      primaryForeground: [183, 100, 96],
    },
    dark: {
      primary: [186, 94, 50],
      primaryForeground: [185, 96, 10],
    },
    preview: 'hsl(189, 94%, 43%)',
  },
  {
    id: 'emerald',
    name: 'Emerald',
    light: {
      primary: [160, 84, 39],
      primaryForeground: [152, 76, 96],
    },
    dark: {
      primary: [160, 84, 45],
      primaryForeground: [156, 72, 10],
    },
    preview: 'hsl(160, 84%, 39%)',
  },
  {
    id: 'fuchsia',
    name: 'Fuchsia',
    light: {
      primary: [292, 84, 61],
      primaryForeground: [289, 100, 98],
    },
    dark: {
      primary: [292, 91, 65],
      primaryForeground: [295, 70, 10],
    },
    preview: 'hsl(292, 84%, 61%)',
  },
  {
    id: 'green',
    name: 'Green',
    light: {
      primary: [142, 71, 45],
      primaryForeground: [138, 76, 97],
    },
    dark: {
      primary: [142, 71, 50],
      primaryForeground: [140, 70, 10],
    },
    preview: 'hsl(142, 71%, 45%)',
  },
  {
    id: 'indigo',
    name: 'Indigo',
    light: {
      primary: [239, 84, 67],
      primaryForeground: [226, 100, 97],
    },
    dark: {
      primary: [239, 84, 67],
      primaryForeground: [230, 80, 10],
    },
    preview: 'hsl(239, 84%, 67%)',
  },
  {
    id: 'lime',
    name: 'Lime',
    light: {
      primary: [84, 81, 44],
      primaryForeground: [82, 85, 10],
    },
    dark: {
      primary: [82, 85, 55],
      primaryForeground: [80, 80, 10],
    },
    preview: 'hsl(84, 81%, 44%)',
  },
  {
    id: 'orange',
    name: 'Orange',
    light: {
      primary: [25, 95, 53],
      primaryForeground: [33, 100, 96],
    },
    dark: {
      primary: [25, 95, 53],
      primaryForeground: [20, 90, 10],
    },
    preview: 'hsl(25, 95%, 53%)',
  },
  {
    id: 'pink',
    name: 'Pink',
    light: {
      primary: [330, 81, 60],
      primaryForeground: [327, 100, 97],
    },
    dark: {
      primary: [330, 81, 65],
      primaryForeground: [328, 80, 10],
    },
    preview: 'hsl(330, 81%, 60%)',
  },
  {
    id: 'red',
    name: 'Red',
    light: {
      primary: [0, 72, 51],
      primaryForeground: [0, 86, 97],
    },
    dark: {
      primary: [0, 72, 55],
      primaryForeground: [0, 70, 10],
    },
    preview: 'hsl(0, 72%, 51%)',
  },
  {
    id: 'rose',
    name: 'Rose',
    light: {
      primary: [347, 77, 50],
      primaryForeground: [343, 100, 97],
    },
    dark: {
      primary: [347, 77, 55],
      primaryForeground: [345, 75, 10],
    },
    preview: 'hsl(347, 77%, 50%)',
  },
  {
    id: 'violet',
    name: 'Violet',
    light: {
      primary: [263, 70, 50],
      primaryForeground: [260, 100, 98],
    },
    dark: {
      primary: [263, 70, 58],
      primaryForeground: [260, 70, 10],
    },
    preview: 'hsl(263, 70%, 50%)',
  },
  {
    id: 'yellow',
    name: 'Yellow',
    light: {
      primary: [48, 96, 53],
      primaryForeground: [45, 92, 10],
    },
    dark: {
      primary: [48, 96, 53],
      primaryForeground: [45, 92, 10],
    },
    preview: 'hsl(48, 96%, 53%)',
  },
]

export function getThemeColorById(id: ThemeColor): ThemeColorDefinition | undefined {
  return THEME_COLORS.find((c) => c.id === id)
}

export function getThemeColorCSS(colorId: ThemeColor, mode: 'light' | 'dark'): string {
  const color = getThemeColorById(colorId)
  if (!color) return ''

  const { primary, primaryForeground } = mode === 'light' ? color.light : color.dark
  return `
    --primary: ${primary[0]} ${primary[1]}% ${primary[2]}%;
    --primary-foreground: ${primaryForeground[0]} ${primaryForeground[1]}% ${primaryForeground[2]}%;
  `
}
